import { withBusyIndicator } from "@components/busyIndicator/withBusyIndicator";
import { withConfirmationDialog } from "@components/dialog/withConfirmationDialog";
import { formatHeaderValue } from "@components/readOnlyList/ReadOnlyList";
import { IReadOnlyListItem } from "@components/readOnlyList/ReadOnlyListItem";
import { getRow } from "@components/smart/smartTable/SmartTable.utils";
import { IRow, TId } from "@components/table";
import { TEntityKey } from "@evala/odata-metadata";
import BindingContext, { createPath, IEntity } from "@odata/BindingContext";
import { UserPermissionCodes } from "@odata/CompanyPermissionEnums";
import { getNewItemsMaxId } from "@odata/Data.utils";
import {
    DocumentEntity,
    DocumentItemLinkableToGrantViewEntity,
    GrantEntity,
    IDocumentItemLinkableToGrantViewEntity,
    IGrantClaimEntity,
    IGrantEntity,
    IPayingDocumentEntity
} from "@odata/GeneratedEntityTypes";
import { DocumentTypeCode } from "@odata/GeneratedEnums";
import { prepareQuery } from "@odata/OData.utils";
import { IPaymentDocumentItemEntityExpanded } from "@pages/banks/bankTransactions/BankTransactions.utils";
import { PairTableViewBaseForExtend } from "@pages/banks/PairTableViewBase";
import { DEFAULT_GRANT_ACCOUNTS, loadPairAccounts, startsWithAccount } from "@pages/grant/Grant.utils";
import i18next from "i18next";

import { withPermissionContext } from "../../contexts/permissionContext/withPermissionContext";
import { TRecordAny } from "../../global.types";
import { IAfterSaveEventArgs, ModelEvent } from "../../model/Model";
import { FormStorage } from "../../views/formView/FormStorage";

class GrantPairTableView extends PairTableViewBaseForExtend {
    _filterLoaded = false;

    get addMenuConfiguration() {
        return [
            { id: "OtherLiabilities", permission: UserPermissionCodes.InvoicesReceived },
            { id: "OtherReceivables", permission: UserPermissionCodes.InvoicesReceived },
            { id: "InternalDocuments", permission: UserPermissionCodes.InternalDocuments }
        ];
    }

    componentDidMount = async () => {
        this.props.storage.emitter.on(ModelEvent.AfterSave, this.handleFormAfterSave);

        const keys = await loadPairAccounts(this.props.storage);
        const defaultKeys = Array.from(keys.keys()).filter(key => {
            return DEFAULT_GRANT_ACCOUNTS.find(defaultKey => {
                return key.startsWith(defaultKey);
            });
        });

        this.props.storage.setFilterValueByPath(DocumentItemLinkableToGrantViewEntity.CreditAccount,
            defaultKeys
        );

        this._filterLoaded = true;
        this.props.storage.applyFilters();
        this.forceUpdate();
    }

    rowsFactory = (rows: IRow[]): IRow[] => {
        return rows;
    };

    canRowBePaired = (rowId: TId) => {
        const row = getRow(this.props.storage.tableAPI.getState().rows, rowId);
        const isPaired = !!this.origPairedDocuments?.[(rowId as BindingContext)?.getKey?.()];

        if (isPaired) {
            return true;
        }

        if (row?.customData) {
            const entity: IDocumentItemLinkableToGrantViewEntity = row.customData.entity as IPayingDocumentEntity;
            const ca = entity[DocumentItemLinkableToGrantViewEntity.CreditAccount]?.toString();
            const da = entity[DocumentItemLinkableToGrantViewEntity.DebitAccount]?.toString();

            const isCa3 = ca?.startsWith("3");
            const isDa3 = da?.startsWith("3");

            if (isCa3 && isDa3) {
                return false;
            }

            const isStartingCa3 = startsWithAccount(ca);
            const isStartingDa3 = startsWithAccount(da);

            if (isStartingCa3 || isStartingDa3) {
                return true;
            }
        }

        return false;
    }

    loadItemsId = async (bc: BindingContext) => {
        const itemsBc = bc.navigate("Items");
        const query = prepareQuery({
            oData: this.props.storage.oData,
            bindingContext: itemsBc,
            fieldDefs: [
                { id: createPath(DocumentEntity.Id) }
            ]
        });

        const result = await query.fetchData();
        return result?.value || [];
    }

    async handleFormAfterSave({ isNew, bindingContext }: IAfterSaveEventArgs) {
        if (isNew) {
            try {
                const ids: TRecordAny[] = await this.loadItemsId(bindingContext);
                this._createdRowBindingContext = new Set(ids.map(item => item.Id));
                this.getInitialActiveRows.reset();
            } catch (e) {
                // nothing just skip pairing
            }
        }
    }

    handleRowSelect = (bc: BindingContext) => {
        const row = getRow(this.props.storage.tableAPI.getState().rows, bc);
        const docId = row?.customData?.entity?.Document?.Id;
        if (docId) {
            const correctBc = bc.createNewBindingContext(this.getRowEntitySet(bc)).addKey(docId);
            this.props.onRowSelect?.(correctBc);
        }
    }

    setOriginItems(): void {
        const { rootStorage, storage } = this.props;
        const entity: IGrantEntity = rootStorage.data.entity;
        const pairedDocuments: Record<TEntityKey, IGrantClaimEntity> = {};

        for (const item of (entity.GrantClaims || [])) {
            const key = item.DocumentItem?.Id;

            pairedDocuments[key] = {
                Id: key
            };
        }

        this.origPairedDocuments = pairedDocuments;

        storage.setCustomData({
            rootStorage,
            pairedDocuments
        });
    }

    get secondaryTitle(): string {
        return i18next.t("Grants:PairEntitlementAndRefund");
    }

    isPairingRowReadOnly = (pairedDoc: IEntity) => {
        return false;
    }

    processRowPairing = async (rowId: TId): Promise<Record<TEntityKey, IPaymentDocumentItemEntityExpanded>> => {
        const { storage, rootStorage } = this.props;
        const bc = rowId as BindingContext;
        const key = bc.getKey();
        const pairedDoc: Record<string, IEntity> = this.getPairedDocuments();

        if (pairedDoc[key]) {
            delete pairedDoc[key];
        } else {
            const es = this.getRowEntitySet(bc);
            if (es) {
                const type = rootStorage.oData.metadata.getTypeForPath(this.getRowEntitySet(bc));
                const row = getRow(storage.tableAPI.getState().rows, bc);
                if (row) {
                    const entity = row?.customData?.entity as IDocumentItemLinkableToGrantViewEntity;
                    const docType = entity.Document?.DocumentType?.Code;
                    const itemName = docType === DocumentTypeCode.InternalDocument ? "InternalDocumentItem" : "RegularDocumentItem";

                    pairedDoc[key] = {
                        Amount: entity.Amount,
                        GrantClaimTypeCode: entity.GrantClaimTypeCode,
                        CurrencyCode: entity.Document.TransactionCurrencyCode,
                        DocumentItem: {
                            DateCreated: entity.DocumentItem?.DateCreated,
                            Description: entity.DocumentItem?.Description,
                            Id: entity.DocumentItem?.Id,
                            _metadata: {
                                "": { type: `#Solitea.Evala.DomainModel.${itemName}` }
                            }
                        },
                        Document: {
                            Id: entity.Document?.Id,
                            DateAccountingTransaction: entity.Document?.DateAccountingTransaction,
                            NumberOurs: entity.Document?.NumberOurs,
                            DocumentType: entity.Document?.DocumentType,
                            DocumentTypeCode: entity.Document?.DocumentType?.Code,
                            _metadata: {
                                "": { type: `#${type.getFullName()}` }
                            }

                        }
                    };
                }
            }
        }

        return pairedDoc;
    }

    getTitle = () => {
        return `${i18next.t(`Grants:Title`)} ${this.props.rootStorage.data.entity[GrantEntity.NumberOurs]}`;
    };

    getHeaderData = (): IReadOnlyListItem[] => {
        const { rootStorage } = this.props;
        const { entity } = rootStorage.data;

        return [{
            label: rootStorage.t("Grants:Name"),
            value: formatHeaderValue(entity[GrantEntity.ShortName])
        }, {
            label: rootStorage.t("Grants:FullName"),
            value: formatHeaderValue(entity[GrantEntity.Name])
        }];
    }

    savePairChanges = async (storage: FormStorage, pairedDocs: Record<TEntityKey, IGrantClaimEntity>): Promise<void> => {
        const entity = storage.data.entity as IGrantEntity;
        const items = [];

        let maxId = getNewItemsMaxId(entity.GrantClaims) + 1;

        for (const [key, link] of Object.entries(pairedDocs)) {
            let item = entity.GrantClaims?.find((item) => item.DocumentItem?.Id?.toString() === key);

            if (!item) {
                item = BindingContext.createNewEntity(maxId++, link);
            }

            items.push(item);
        }

        storage.data.entity[GrantEntity.GrantClaims] = items;
        storage.handleFirstChange();
    };

    render() {
        if (this._filterLoaded) {
            return super.render();
        }

        return null;
    }

}

export default withPermissionContext(withBusyIndicator({
    isDelayed: true,
    passBusyIndicator: true
})(withConfirmationDialog(GrantPairTableView)));